import type { ComponentType } from 'react';
import { Suspense, lazy, useState } from 'react';

import { staleChunkRecovery } from '../AppVersion/staleChunkRecoveryInstance';
import { ChatErrorBoundary } from '../../components/Chats/ChatErrorBoundary';

type ChatsModule = { Chats: ComponentType };
type ChunkRecovery = Pick<
  typeof staleChunkRecovery,
  'onChatImportError' | 'onChatImportSuccess'
>;

/**
 * The chat chunk's loader, wired into the same stale-chunk recovery the route
 * loader uses (`router/routes/lazyRoute.ts`): a deploy that replaced the
 * hashed chunks this tab was built against gets ONE guarded reload instead of
 * a chat that silently never appears. `.then` unwraps the named export, which
 * is what `React.lazy` needs from a module that has no default one.
 */
export const createChatsLoader =
  (importChats: () => Promise<ChatsModule>, recovery: ChunkRecovery) => () =>
    importChats().then(
      (module) => {
        recovery.onChatImportSuccess();
        return { default: module.Chats };
      },
      (error: unknown) => {
        // A recovery reload is in progress — never settle, so the boundary
        // does not flash while the page restarts.
        if (recovery.onChatImportError(error))
          return new Promise<never>(() => {});
        throw error;
      },
    );

const loadChats = createChatsLoader(
  () => import('../Chats'),
  staleChunkRecovery,
);

interface LazyChatsProps {
  /** Injectable for tests; the app always uses the real chunk. */
  load?: () => Promise<{ default: ComponentType }>;
}

/**
 * The header's chat, behind its own error boundary and Suspense.
 *
 * `React.lazy` caches its FIRST import promise, rejection included, so a
 * single failed chunk request used to hide chat until a full page reload:
 * the boundary's 30 s reset re-rendered the same rejected component and never
 * asked for the chunk again (review 2026-10-01 follow-up #4). The lazy
 * component therefore lives in state and is recreated on every reset — a
 * fresh `lazy()` issues a fresh `import()`, which the browser serves from its
 * module cache once the chunk has loaded.
 *
 * The availability gate stays with the caller, OUTSIDE this component: a gate
 * inside the lazy boundary would run only after the chunk had downloaded.
 */
export function LazyChats({ load = loadChats }: LazyChatsProps) {
  const [Chats, setChats] = useState(() => lazy(load));

  return (
    <ChatErrorBoundary onReset={() => setChats(() => lazy(load))}>
      <Suspense fallback={null}>
        <Chats />
      </Suspense>
    </ChatErrorBoundary>
  );
}
